import { ExIcon, IconSize } from '@boomi/exosphere';
import { useId, useEffect } from 'react';
import { useApp } from '../common/AppProvider';
import Option from './Option';
import { translations } from '../translations/translations';
import type { Action, OptionValueType, Output, Trigger } from '../types/types';
import { stringReplace } from '../utils/utils';

import './trigger-or-action-configuration.css';

const TriggerOrActionConfiguration = ({
    itemData,
    onChange,
    showValidation,
    outputs,
}: {
    itemData: Trigger | Action;
    onChange: (id: string, value: OptionValueType) => void;
    showValidation: boolean;
    outputs: Output[];
}) => {
    const uniqueID = useId(); // random unique IDs used for config options
    const { connectors, language } = useApp();
    const connector = connectors[itemData.providerId];

    const { options, externalConfiguration, externalConfigurationHelpUrl } = itemData;

    const isBlankItemData = itemData ? !Object.hasOwn(itemData, 'id') : true;
    const noConfigRequired = !isBlankItemData && !(externalConfiguration || options?.length);

    const requiredConfig = options ? options.filter((filter) => filter.isRequired) : [];
    const optionalConfig = options ? options.filter((filter) => !filter.isRequired) : [];

    // In slackcreatereminder when frequency is other than Weekly clear Weekdays.
    useEffect(() => {
        if (itemData.id === 'slackcreatereminder') {
            const { frequency, weekdays } = itemData.values;

            if (
                frequency !== undefined &&
                frequency !== 'weekly' &&
                weekdays !== undefined &&
                (weekdays as string[]).length > 0
            ) {
                onChange('weekdays', []);
            }
        }
    }, [itemData, onChange]);

    return (
        <div className="task-item-configuration-form">
            {/* External configuration */}
            {externalConfiguration && (
                <div className="external-config">
                    <p className="text md">
                        {stringReplace(
                            translations[language].TASK_external_configuration_info,
                            connector.displayName,
                        )}
                    </p>
                    <pre className="text md">{externalConfiguration}</pre>
                    {externalConfigurationHelpUrl && (
                        <p className="text md">
                            {translations[language].TASK_external_configuration_link}{' '}
                            <a
                                href={externalConfigurationHelpUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {connector.displayName} Documentation
                            </a>
                            {'.'}
                        </p>
                    )}
                </div>
            )}
            {/* Required configuration */}
            {!!requiredConfig?.length && (
                <div id={`${itemData.id}RequiredCfg`}>
                    {requiredConfig.map((option) => (
                        <Option
                            key={uniqueID + option.id}
                            option={option}
                            value={itemData.values[option.id]}
                            outputs={outputs}
                            onChange={onChange}
                            showValidation={showValidation}
                            language={language}
                        />
                    ))}
                </div>
            )}
            {/* Optional configuration */}
            {!!optionalConfig?.length && (
                <div id={`${itemData.id}OptionalCfg`}>
                    {optionalConfig.map((option) => (
                        <Option
                            key={uniqueID + option.id}
                            option={option}
                            value={itemData.values[option.id]}
                            outputs={outputs}
                            onChange={onChange}
                            showValidation={showValidation}
                            language={language}
                        />
                    ))}
                </div>
            )}
            {/* No config is needed */}
            {noConfigRequired && (
                <p className="config-message">
                    <ExIcon icon="Information" size={IconSize.M} hideBrowserTooltip />
                    {translations[language].TASK_no_config_required}
                </p>
            )}
            {/* No trigger or action have been selected yet */}
            {isBlankItemData && (
                <p className="config-message">
                    <ExIcon icon="Information" size={IconSize.M} hideBrowserTooltip />
                    {translations[language].TASK_nothing_selected}
                </p>
            )}
        </div>
    );
};

export default TriggerOrActionConfiguration;
